import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import portal, { ReporteDePago } from "../../../services/v2/portalAlumno.service";
import { ListaDeReportes } from "./portalComun";
import "../../public/pagar/pagar.css";
import "./portal.css";

/*
	Reportar Mi Pago (portal del alumno): el mismo ticket que /pagar/<plantel> pero con el alumno ya
	identificado por su sesión: no se pide nombre, teléfono ni correo y el servidor ignora cualquier
	id que mande el cliente. Cae en Pagos por Procesar con "Lo envió el alumno". Reusa el look de /pagar
	(clases pg*), embebido: .pgEmbebido le quita el modo pantalla completa.
*/
const MAX_ARCHIVOS = 5
const MAX_BYTES = 10 * 1024 * 1024

interface Archivo { id: string; file: File; vista: string | null }

const peso = (b: number) => b >= 1024 * 1024 ? (b / 1024 / 1024).toFixed(1) + " MB" : Math.max(1, Math.round(b / 1024)) + " KB"
const esValido = (f: File) => /^image\//.test(f.type) || f.type === "application/pdf" || /\.(jpe?g|png|webp|heic|heif|pdf)$/i.test(f.name)

const ReportarMiPago: React.FC = () => {
	const [motivo, setMotivo] = useState("")
	const [archivos, setArchivos] = useState<Archivo[]>([])
	const [aviso, setAviso] = useState<string | null>(null)
	const [enviando, setEnviando] = useState(false)
	const [avance, setAvance] = useState(0)
	const [folio, setFolio] = useState<number | null>(null)
	const [reportes, setReportes] = useState<ReporteDePago[]>([])
	const entrada = useRef<HTMLInputElement>(null)

	const cargarReportes = () => { portal.reports().then((r) => setReportes(r.data.reports)).catch(() => { /* la lista es un extra */ }) }
	useEffect(() => { cargarReportes() }, [])
	useEffect(() => () => { archivos.forEach((a) => a.vista && URL.revokeObjectURL(a.vista)) }, []) // eslint-disable-line react-hooks/exhaustive-deps

	const agregar = (lista: FileList | null) => {
		if (!lista) return
		const nuevos: Archivo[] = []
		for (const f of Array.from(lista)) {
			if (!esValido(f)) { setAviso("El comprobante tiene que ser foto o PDF."); continue }
			if (f.size > MAX_BYTES) { setAviso(f.name + " pesa más de 10 MB."); continue }
			nuevos.push({ id: Math.random().toString(36).slice(2), file: f, vista: /^image\/(jpe?g|png|webp|gif)$/.test(f.type) ? URL.createObjectURL(f) : null })
		}
		setArchivos((a) => {
			const todo = [...a, ...nuevos]
			if (todo.length > MAX_ARCHIVOS) setAviso("Puedes subir hasta " + MAX_ARCHIVOS + " comprobantes.")
			return todo.slice(0, MAX_ARCHIVOS)
		})
		if (entrada.current) entrada.current.value = ""
	}

	const quitar = (id: string) => setArchivos((a) => {
		const x = a.find((y) => y.id === id)
		if (x?.vista) URL.revokeObjectURL(x.vista)
		return a.filter((y) => y.id !== id)
	})

	const enviar = async (e: React.FormEvent) => {
		e.preventDefault()
		setAviso(null)
		if (archivos.length < 1) { setAviso("Sube la foto o el PDF de tu comprobante."); return }
		setEnviando(true)
		setAvance(0)
		try {
			const r = await portal.reportPayment(motivo.trim(), archivos.map((a) => a.file), setAvance)
			setFolio(r.data.folio)
			cargarReportes()
		} catch (err: any) {
			const st = err?.response?.status
			setAviso(st === 429 ? "Ya enviaste varios reportes seguidos. Espera un minuto e intenta otra vez."
				: err?.response?.data?.message || (err?.response ? "No pudimos recibir tu pago. Intenta otra vez." : "No hay conexión. Revisa tu internet e intenta otra vez."))
		} finally {
			setEnviando(false)
		}
	}

	const otroPago = () => {
		archivos.forEach((a) => a.vista && URL.revokeObjectURL(a.vista))
		setArchivos([]); setMotivo(""); setFolio(null); setAviso(null)
	}

	return (
		<div className="pt">
			<div className="pg pgEmbebido">
				{folio !== null ? (
					<div className="pgCuerpo pgListo pgEntra">
						<div className="pgPalomita"><FontAwesomeIcon icon={solid("check")} /></div>
						<h1 className="pgTitulo">¡Listo!</h1>
						<p className="pgBajada">Recibimos tu pago, Caja lo va a revisar.</p>
						{folio > 0 ? <div className="pgFolio"><span>Tu folio</span><b>#{folio}</b></div> : null}
						<button type="button" className="pgBoton secundario" onClick={otroPago}><span>Enviar Otro Pago</span></button>
					</div>
				) : (
					<div className="pgCuerpo pgEntra">
						<h1 className="pgTitulo">Reportar Mi Pago</h1>
						{aviso ? <div className="pgAviso" role="alert"><FontAwesomeIcon icon={solid("circle-exclamation")} /><span>{aviso}</span></div> : null}
						<form onSubmit={enviar} noValidate>
							<div className="pgTarjeta">
								<div className="pgCampo">
									<label className="pgEtiqueta" htmlFor="pt-motivo">Motivo Del Pago <small>(opcional)</small></label>
									<textarea id="pt-motivo" className="pgInput" rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={500} placeholder="Ej. Mensualidad de octubre" />
								</div>
							</div>
							<div className="pgTarjeta">
								<span className="pgEtiqueta">Comprobante</span>
								{archivos.map((a) => (
									<div key={a.id} className="pgArchivo">
										<div className="pgMiniatura">{a.vista ? <img src={a.vista} alt="" /> : <FontAwesomeIcon icon={solid("file-lines")} />}</div>
										<div className="pgArchivoTexto">
											<div className="pgArchivoNombre">{a.file.name}</div>
											<div className="pgArchivoPeso">{peso(a.file.size)}</div>
										</div>
										<button type="button" className="pgQuitar" aria-label={"Quitar " + a.file.name} disabled={enviando} onClick={() => quitar(a.id)}><FontAwesomeIcon icon={solid("xmark")} /></button>
									</div>
								))}
								{archivos.length < MAX_ARCHIVOS ? (
									<button type="button" className={"pgSubir" + (archivos.length ? " chico" : "")} disabled={enviando} onClick={() => entrada.current?.click()}>
										<FontAwesomeIcon icon={archivos.length ? solid("plus") : solid("camera")} />
										{archivos.length ? "Agregar Otro" : "Subir Comprobante"}
										{archivos.length ? null : <small>Foto o PDF</small>}
									</button>
								) : null}
								<input ref={entrada} type="file" accept="image/*,application/pdf" multiple hidden onChange={(e) => agregar(e.target.files)} />
							</div>
							<button type="submit" className="pgBoton" disabled={enviando}>
								{enviando ? <><span className="pgAvance" style={{ width: avance + "%" }} /><span className="pgGiro" /><span>{avance < 100 ? "Enviando… " + avance + "%" : "Enviando…"}</span></> : <span>Enviar</span>}
							</button>
						</form>
					</div>
				)}
				<div className="pgCuerpo ptReportesCuerpo">
					<h2 className="ptH2">Mis Reportes</h2>
					<ListaDeReportes reportes={reportes} />
				</div>
			</div>
		</div>
	)
}

export default ReportarMiPago
